import { type Artist, type ShowWithDetails, type ArtistWithShows } from "@shared/schema";
import { type AdvancedFilters } from "@/components/advanced-filter";
import { normalizeCareerStage } from "@/modules/calculator";
import { PRIMARY_GENRE_COUNT } from "@shared/genre-utils";

export interface ShowWithMatch extends ShowWithDetails {
  audienceMatchPercentage: number;
  matchingAttributes: string[];
  nonMatchingAttributes: string[];
  totalCriteria: number;
  matchedCriteria: number;
}

export interface ArtistWithMatch extends ArtistWithShows {
  audienceMatchPercentage: number;
  matchingAttributes: string[];
  nonMatchingAttributes: string[];
  totalCriteria: number;
  matchedCriteria: number;
}

/**
 * Calculate audience match percentage between target filters and artist demographics
 */
export function calculateAudienceMatch(
  artist: Artist,
  targetAudience: AdvancedFilters['targetAudience'],
  requiredFields?: {
    requiredAgeRanges?: string[];
    requiredGenders?: string[];
    requiredEthnicities?: string[];
    requiredIncomeLevels?: string[];
    requiredTargetLocations?: string[];
    requiredLifestyleAttributes?: string[];
    requiredMusicGenres?: string[];
  }
): {
  percentage: number;
  matchingAttributes: string[];
  nonMatchingAttributes: string[];
  totalCriteria: number;
  matchedCriteria: number;
  passesRequiredFields: boolean;
} {
  let totalCriteria = 0;
  let matchedCriteria = 0;
  const matchingAttributes: string[] = [];
  const nonMatchingAttributes: string[] = [];
  let passesRequiredFields = true;

  // Helper function to extract titles from JSONB array where value > 1.05 (over-indexing)
  // or parse comma-separated values for legacy format
  const extractValues = (value: any, isGeoData: boolean = false): string[] => {
    if (!value) return [];

    // Handle JSONB array format
    if (Array.isArray(value)) {
      // For geo data, filter by score (combination of affinity and audience size)
      if (isGeoData) {
        // Use score > 0.2 as threshold for meaningful markets
        // This captures markets with good combination of affinity and audience size
        return value
          .filter(item => item.score && item.score > 0.2)
          .map(item => (item.title || '').toLowerCase());
      }

      // For demographic data, only include items where value > 1.05 (over-indexing)
      // Threshold raised from 1.0 to 1.05 to filter out noise in the 1.0-1.05 range
      return value
        .filter(item => item.value && item.value > 1.05)
        .map(item => (item.title || '').toLowerCase());
    }

    // Fallback to comma-separated string format for legacy data
    if (typeof value === 'string') {
      return value.split(',').map(item => item.trim().toLowerCase());
    }

    return [];
  };

  // Helper function to check if target values match artist values using AND logic
  const checkMatch = (artistValues: string[], targetValues: string[], category: string) => {
    if (!targetValues || targetValues.length === 0) return { matched: false, count: 0 };

    // Count how many target values have matches in artist values
    let matchedTargetCount = 0;

    targetValues.forEach(targetValue => {
      const normalizedTarget = targetValue.toLowerCase();
      const hasMatch = artistValues.some(artistValue => {
        const normalizedArtist = artistValue.toLowerCase();
        if (normalizedArtist.includes(normalizedTarget)) return true;
        // Reverse: only allow if artist value has 2+ tokens (avoid single-word false positives)
        const artistTokens = normalizedArtist.trim().split(/\s+/);
        return artistTokens.length >= 2 && normalizedTarget.includes(normalizedArtist);
      });

      if (hasMatch) {
        matchedTargetCount++;
        // Add individual matched attribute (no category label)
        matchingAttributes.push(targetValue);
      } else {
        // Add individual non-matched attribute (no category label)
        nonMatchingAttributes.push(targetValue);
      }
    });

    // Add to total criteria count (each individual target value is a criterion)
    totalCriteria += targetValues.length;
    matchedCriteria += matchedTargetCount;

    return { matched: matchedTargetCount > 0, count: matchedTargetCount };
  };

  // Check Fan Gender
  const artistGender = extractValues(artist.fanGender, false);
  checkMatch(artistGender, targetAudience.fanGender, 'Gender');

  // Check Fan Ethnicity
  const artistEthnicity = extractValues(artist.fanEthnicity, false);
  checkMatch(artistEthnicity, targetAudience.fanEthnicity, 'Ethnicity');

  // Check Fan Age
  const artistAge = extractValues(artist.fanAge, false);
  checkMatch(artistAge, targetAudience.fanAge, 'Age');

  // Check Fan Income
  const artistIncome = extractValues(artist.fanIncome, false);
  checkMatch(artistIncome, targetAudience.fanIncome, 'Income');

  // Check Fan Attributes
  const artistAttributes = extractValues(artist.fanAttributes, false);
  checkMatch(artistAttributes, targetAudience.fanAttributes, 'Attributes');

  // NOTE: Geography/DMAs are now HARD FILTERS and do NOT contribute to match percentage
  // They are applied via filterByDmaIds in the filter engine, not here

  // Check required fields - artist must match ALL required values
  if (requiredFields) {
    // Helper to check if ALL required values are present in artist data
    const checkRequired = (artistValues: string[], requiredValues: string[] | undefined): boolean => {
      if (!requiredValues || requiredValues.length === 0) return true;

      // All required values must have a match
      return requiredValues.every(requiredValue => {
        const normalizedRequired = requiredValue.toLowerCase();
        return artistValues.some(artistValue => {
          const normalizedArtist = artistValue.toLowerCase();
          if (normalizedArtist.includes(normalizedRequired)) return true;
          // Reverse: only allow if artist value has 2+ tokens (avoid single-word false positives)
          const artistTokens = normalizedArtist.trim().split(/\s+/);
          return artistTokens.length >= 2 && normalizedRequired.includes(normalizedArtist);
        });
      });
    };

    // Check each required field
    const artistGender = extractValues(artist.fanGender, false);
    const artistEthnicity = extractValues(artist.fanEthnicity, false);
    const artistAge = extractValues(artist.fanAge, false);
    const artistIncome = extractValues(artist.fanIncome, false);
    const artistAttributes = extractValues(artist.fanAttributes, false);

    if (!checkRequired(artistGender, requiredFields.requiredGenders)) passesRequiredFields = false;
    if (!checkRequired(artistEthnicity, requiredFields.requiredEthnicities)) passesRequiredFields = false;
    if (!checkRequired(artistAge, requiredFields.requiredAgeRanges)) passesRequiredFields = false;
    if (!checkRequired(artistIncome, requiredFields.requiredIncomeLevels)) passesRequiredFields = false;
    if (!checkRequired(artistAttributes, requiredFields.requiredLifestyleAttributes)) passesRequiredFields = false;
    // NOTE: Geography/DMAs are now HARD FILTERS handled by filterByDmaIds, not required fields
  }

  // Calculate percentage
  const percentage = totalCriteria > 0 ? Math.round((matchedCriteria / totalCriteria) * 100) : 0;

  return {
    percentage,
    matchingAttributes,
    nonMatchingAttributes,
    totalCriteria,
    matchedCriteria,
    passesRequiredFields
  };
}

/**
 * Apply hard filters to shows
 */
export function applyHardFilters(shows: ShowWithDetails[], filters: AdvancedFilters): ShowWithDetails[] {
  return shows.filter(show => {
    // Location filter
    if (filters.location) {
      const loc = filters.location.toLowerCase();
      const locationMatch =
        (show.venue.city || '').toLowerCase().includes(loc) ||
        (show.venue.state || '').toLowerCase().includes(loc) ||
        (show.venue.country || '').toLowerCase().includes(loc);
      if (!locationMatch) return false;
    }

    // Genre filter (only primary genres)
    if (filters.genre && filters.genre.length > 0) {
      const primaryGenres = (show.artist.genres || []).slice(0, PRIMARY_GENRE_COUNT);
      const genreMatch = primaryGenres.some(genre =>
        filters.genre.some(filterGenre =>
          genre.toLowerCase().includes(filterGenre.toLowerCase())
        )
      );
      if (!genreMatch) return false;
    }

    // Career Stage filter
    if (filters.careerStage && filters.careerStage.length > 0) {
      const artistStage = normalizeCareerStage(show.artist.careerStage || "").toLowerCase().replace(/\s+/g, '-');
      const stageMatch = filters.careerStage.some(filterStage =>
        artistStage === filterStage.toLowerCase()
      );
      if (!stageMatch) return false;
    }

    // Venue Type filter
    if (filters.venueType && filters.venueType.length > 0) {
      const vt = show.venue.venueType;
      if (!vt) return false;
      if (!filters.venueType.some(t => vt.toLowerCase() === t.toLowerCase())) return false;
    }

    // Date filters (date-only)
    if (filters.dateFrom || filters.dateTo) {
      const { isInDateRange } = require('@shared/date-utils');
      const dateKey = (show.date as any as string).slice(0,10);
      if (!isInDateRange(dateKey, filters.dateFrom, filters.dateTo)) return false;
    }

    return true;
  });
}

/**
 * Apply audience filters and calculate match percentages for shows
 */
export function applyAudienceFilters(shows: ShowWithDetails[], filters: AdvancedFilters): ShowWithMatch[] {
  const showsWithMatch: ShowWithMatch[] = shows.map(show => {
    const match = calculateAudienceMatch(show.artist, filters.targetAudience);
    return {
      ...show,
      audienceMatchPercentage: match.percentage,
      matchingAttributes: match.matchingAttributes,
      nonMatchingAttributes: match.nonMatchingAttributes,
      totalCriteria: match.totalCriteria,
      matchedCriteria: match.matchedCriteria
    };
  });

  // Filter by minimum match percentage
  const filteredShows = showsWithMatch.filter(show => 
    show.audienceMatchPercentage >= filters.minMatchPercentage
  );

  // Sort by match percentage (highest first)
  return filteredShows.sort((a, b) => b.audienceMatchPercentage - a.audienceMatchPercentage);
}

/**
 * Apply audience filters and calculate match percentages for artists
 */
export function applyAudienceFiltersToArtists(artists: ArtistWithShows[], filters: AdvancedFilters): ArtistWithMatch[] {
  // First, filter artists by hard filters (location, genre, date range, career stage)
  const hardFilteredArtists = artists.filter(artist => {
    // Career Stage filter (apply to artist level first)
    if (filters.careerStage && filters.careerStage.length > 0) {
      const artistStage = normalizeCareerStage(artist.careerStage || "").toLowerCase().replace(/\s+/g, '-');
      const stageMatch = filters.careerStage.some(filterStage => 
        artistStage === filterStage.toLowerCase()
      );
      if (!stageMatch) return false;
    }
    
    // Check if artist has any shows that match hard filters
    const matchingShows = artist.upcomingShows.filter(show => {
      // Location filter
      if (filters.location) {
        const loc = filters.location.toLowerCase();
        const locationMatch =
          (show.venue.city || '').toLowerCase().includes(loc) ||
          (show.venue.state || '').toLowerCase().includes(loc) ||
          (show.venue.country || '').toLowerCase().includes(loc);
        if (!locationMatch) return false;
      }

      // Genre filter (only primary genres)
      if (filters.genre && filters.genre.length > 0) {
        const primaryGenres = (artist.genres || []).slice(0, PRIMARY_GENRE_COUNT);
        const genreMatch = primaryGenres.some(genre =>
          filters.genre.some(filterGenre =>
            genre.toLowerCase().includes(filterGenre.toLowerCase())
          )
        );
        if (!genreMatch) return false;
      }

      // Venue Type filter
      if (filters.venueType && filters.venueType.length > 0) {
        const vt = show.venue.venueType;
        if (!vt) return false;
        if (!filters.venueType.some(t => vt.toLowerCase() === t.toLowerCase())) return false;
      }

      // Date filters (date-only)
      if (filters.dateFrom || filters.dateTo) {
        const { isInDateRange } = require('@shared/date-utils');
        const dateKey = (show.date as any as string).slice(0,10);
        if (!isInDateRange(dateKey, filters.dateFrom, filters.dateTo)) return false;
      }

      return true;
    });

    // Keep artist if they have at least one matching show
    if (matchingShows.length > 0) {
      // Update artist's upcomingShows to only include matching shows
      artist.upcomingShows = matchingShows;
      return true;
    }
    return false;
  });

  // Then calculate audience matching
  const artistsWithMatch: ArtistWithMatch[] = hardFilteredArtists.map(artist => {
    const match = calculateAudienceMatch(
      artist,
      filters.targetAudience,
      (filters as any).requiredFields // Pass required fields if they exist
    );
    return {
      ...artist,
      audienceMatchPercentage: match.percentage,
      matchingAttributes: match.matchingAttributes,
      nonMatchingAttributes: match.nonMatchingAttributes,
      totalCriteria: match.totalCriteria,
      matchedCriteria: match.matchedCriteria,
      passesRequiredFields: match.passesRequiredFields
    };
  });

  // Filter by required fields first (hard filter)
  const passesRequired = artistsWithMatch.filter(artist =>
    (artist as any).passesRequiredFields !== false
  );

  // Then filter by minimum match percentage
  const filteredArtists = passesRequired.filter(artist =>
    artist.audienceMatchPercentage >= filters.minMatchPercentage
  );

  // Sort by match percentage (highest first)
  return filteredArtists.sort((a, b) => b.audienceMatchPercentage - a.audienceMatchPercentage);
}

/**
 * Filter a flat array of shows by audience match percentage.
 * Keeps artists without demographic data (avoids false 0% from lightweight queries).
 *
 * @param shows - Array of shows (each must have `.artist`)
 * @param targetAudience - The audience demographic filters to match against
 * @param minMatchPercentage - Minimum match % to keep (0% matches are always excluded when audience filters are active)
 * @returns Filtered shows that pass audience matching
 */
export function filterShowsByAudienceMatch<T extends { artist: any }>(
  shows: T[],
  targetAudience?: AdvancedFilters['targetAudience'],
  minMatchPercentage: number = 0,
): T[] {
  if (!targetAudience) return shows;

  // Check if any audience filters are actually active
  const hasAudienceFilters =
    (targetAudience.fanGender && targetAudience.fanGender.length > 0) ||
    (targetAudience.fanEthnicity && targetAudience.fanEthnicity.length > 0) ||
    (targetAudience.fanAge && targetAudience.fanAge.length > 0) ||
    (targetAudience.fanIncome && targetAudience.fanIncome.length > 0) ||
    (targetAudience.fanAttributes && targetAudience.fanAttributes.length > 0);

  if (!hasAudienceFilters) return shows;

  const effectiveMin = Math.max(1, minMatchPercentage || 0);

  return shows.filter(show => {
    const artist = show.artist as any;
    // If artist has no demographic data (lightweight query), keep it to avoid false 0%
    const hasArtistDemographics = artist?.fanGender || artist?.fanEthnicity ||
                                   artist?.fanAge || artist?.fanIncome || artist?.fanAttributes;
    if (!hasArtistDemographics) return true;

    const matchResult = calculateAudienceMatch(show.artist, targetAudience);
    return matchResult.percentage >= effectiveMin;
  });
}

/**
 * Get default filters
 */
export function getDefaultAdvancedFilters(): AdvancedFilters {
  return {
    artistName: "",
    venueName: "",
    location: "",
    dateFrom: "",
    dateTo: "",
    genre: [],
    careerStage: [],
    setArtist: "",
    venueType: [],
    venueLocation: "",
    targetAudience: {
      fanGender: [],
      fanEthnicity: [],
      fanAge: [],
      fanIncome: [],
      fanAttributes: [],
      fanGeo: []
    },
    minMatchPercentage: 0
  };
}
